import { Controller } from "@hotwired/stimulus"
import { isAbortError, rethrowUnlessAbort } from "../../utils/fetch-error.util"
import { showToast } from "../../utils/toast.util"

export default class extends Controller {
  static targets = ["button"]
  static values = {
    createUrl: String,
    // Starts the download on connect and removes the element when it is over:
    // the issue-first "Vystaviť a stiahnuť" appends such an element once the
    // document is issued (LifecycleTransitionsController then=download).
    auto: { type: Boolean, default: false },
    // { failed: "…" } — when present, a failed download says so in a toast.
    // Only the draft-download dialog and the auto element pass it: both act
    // away from a visible button, so nothing else would tell the user.
    translations: { type: Object, default: {} }
  }

  connect() {
    this.abortController = null
    this.isDownloading = false
    if (this.autoValue) this.#download()
  }

  disconnect() {
    this.abortController?.abort()
  }

  submit(event) {
    event.preventDefault()
    return this.#download()
  }

  // Events, for the draft-download dialog: "documents--pdf-download:done" only
  // once the file was handed to the browser (the dialog closes on it — closing
  // on submit would disconnect this controller and abort the fetch), and
  // "…:failed" when it was not (the dialog stays open, button re-enabled).
  async #download() {
    if (this.isDownloading) return

    this.isDownloading = true
    this.abortController = new AbortController()
    this.#setButtonLoadingState()
    this.#dispatchTooltipEvent("tooltip:set-loading")

    try {
      const response = await this.#requestPdf()
      const blob = await response.blob()
      this.#triggerBrowserDownload(blob, this.#extractFilename(response.headers.get("Content-Disposition")))
      if (this.element.isConnected) this.dispatch("done")
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("PDF download failed:", error)
        this.#reportFailure()
      }
      // console.error alone reaches nothing — browser console collection is off
      // in Better Stack, so re-raise to hit the global handler.
      rethrowUnlessAbort(error)
    } finally {
      this.#dispatchTooltipEvent("tooltip:reset")
      this.#setButtonDefaultState()
      this.isDownloading = false
      this.abortController = null
      if (this.autoValue) this.element.remove()
    }
  }

  #reportFailure() {
    if (this.element.isConnected) this.dispatch("failed")
    showToast({ type: "error", message: this.translationsValue.failed })
  }

  async #requestPdf() {
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/pdf",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": this.#csrfToken()
      },
      credentials: "same-origin",
      signal: this.abortController.signal
    })

    if (!response.ok) {
      throw new Error(`Unable to download PDF (${response.status})`)
    }

    return response
  }

  #dispatchTooltipEvent(name) {
    if (!this.hasButtonTarget) return
    this.buttonTarget.dispatchEvent(new CustomEvent(name, { bubbles: true }))
  }

  #setButtonLoadingState() {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = true
    this.buttonTarget.setAttribute("aria-busy", "true")
    this.buttonTarget.classList.add("is-loading")
  }

  #setButtonDefaultState() {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = false
    this.buttonTarget.removeAttribute("aria-busy")
    this.buttonTarget.classList.remove("is-loading")
  }

  #csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
    return token || ""
  }

  #extractFilename(contentDisposition) {
    if (!contentDisposition) return "document.pdf"

    const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
    const encodedName = match?.[1]
    const plainName = match?.[2]
    const filename = encodedName ? decodeURIComponent(encodedName) : plainName

    return filename || "document.pdf"
  }

  #triggerBrowserDownload(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    link.style.display = "none"
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}
